import { ReactElement, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip } from "antd";
import Button from "../../components/Button";
import loadingSpinner from "../../assets/images/LoadingSpinner.gif";
import PDFFileIcon from "../../assets/svg-tsx/PDFFileIcon";
import FileIcon from "../../assets/svg-tsx/FileIcon";
import DownloadIcon from "../../assets/svg-tsx/DownloadIcon";
import LeftArrowIcon from "../../assets/svg-tsx/LeftArrowIcon";
import Modal from "../../components/common/Modal";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import TextArea from "../../components/TextArea";
import { getBriefDetails, reviewBrief } from "../../api/briefManagement";
import Notfound from "../404/components/Notfound";

// Status → display label + pill classes (mirrors customer-app BRIEF_STATUS_DISPLAY).
const STATUS_DISPLAY: Record<
  string,
  { label: string; pillClass: string }
> = {
  PENDING: {
    label: "IN-REVIEW",
    pillClass: "bg-[#FFDF8680] text-[#7A5800]",
  },
  APPROVED: {
    label: "ACTIVE",
    pillClass: "bg-[#04B36B33] text-[#0A6B43]",
  },
  REJECTED: {
    label: "REJECTED",
    pillClass: "bg-[#EA191933] text-[#A61414]",
  },
  EXPIRED: {
    label: "EXPIRED",
    pillClass: "bg-[#E3E3E3] text-[#5E5E5E]",
  },
};

const INTEREST_DISPLAY: Record<
  string,
  { label: string; className: string }
> = {
  PENDING: {
    label: "AWAITING",
    className: "bg-[#F5F5F5] text-[#7B7B7B]",
  },
  INTERESTED: {
    label: "INTERESTED",
    className: "bg-[#04B36B33] text-[#0A6B43]",
  },
  NOT_INTERESTED: {
    label: "NOT INTERESTED",
    className: "bg-[#EA191933] text-[#A61414]",
  },
};

// Render the date straight from the ISO string without timezone conversion.
// Backend uses Postgres NOW() but labels timestamps "+00:00", so going through
// Date() + locale TZ conversion would shift the displayed day.
const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const formatDateLong = (iso?: string | null) => {
  if (!iso) return null;
  const datePart = iso.split("T")[0];
  const parts = datePart.split("-");
  if (parts.length !== 3) return null;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day || month < 1 || month > 12) return null;
  return `${String(day).padStart(2, "0")} ${MONTHS_SHORT[month - 1]} ${year}`;
};

const formatBriefDateTime = (iso?: string | null) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const day = d.getUTCDate();
  const suffix = ((n: number) => {
    if (n % 100 >= 11 && n % 100 <= 13) return "th";
    if (n % 10 === 1) return "st";
    if (n % 10 === 2) return "nd";
    if (n % 10 === 3) return "rd";
    return "th";
  })(day);
  const month = d.toLocaleString("en-US", { month: "long", timeZone: "UTC" });
  const time = d.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });
  return `${day}${suffix} ${month}, ${time}`;
};

const formatArea = (area?: string | null) => {
  if (!area) return null;
  const num = Number(area);
  if (Number.isNaN(num)) return `${area} sq ft`;
  const clean = Number.isInteger(num) ? num : Number(num.toFixed(2));
  return `${clean.toLocaleString("en-IN")} sq ft`;
};

const fileExtension = (path: string) =>
  (path?.split(".")?.pop() || "").toUpperCase();

type BriefMedia = {
  id: string;
  file: string;
  download_url: string | null;
  filename: string;
  file_size?: string;
  file_type: "image" | "document";
  created_at?: string;
};

type BriefInterest = {
  id: string;
  professional_name: string | null;
  response: "PENDING" | "INTERESTED" | "NOT_INTERESTED" | string;
  responded_at: string | null;
  // WhatsApp per-recipient delivery state, populated from Interakt's status
  // webhooks. delivery_status is null when (a) the row pre-dates the column
  // or (b) the Interakt send itself failed (in which case wa_msg_id is also
  // null and we render "Not sent" instead of any tick).
  delivery_status: "SENT" | "DELIVERED" | "READ" | "FAILED" | null;
  delivery_status_at: string | null;
  wa_msg_id: string | null;
};

// WhatsApp-style tick mark next to each interested-pro row. Mirrors the
// visual vocabulary the recipient saw on their phone:
//   ✓     grey   single tick   — Interakt accepted (SENT)
//   ✓✓    grey   double tick   — delivered to device (DELIVERED)
//   ✓✓    blue   double tick   — opened on device (READ)
//   ⚠     red    cross         — send failed at Interakt (FAILED)
//   ⚠     red    "Not sent"    — Interakt never accepted the send (wa_msg_id
//                                 null) so no webhook will ever arrive
//   —     grey   dash          — older row that pre-dates this column
const DeliveryTicks = ({
  status,
  hasMsgId,
  at,
}: {
  status: BriefInterest["delivery_status"];
  hasMsgId: boolean;
  at: string | null;
}) => {
  // Tick path used for both single & double tick states.
  const tickPath = (
    <path
      d="M1.2 5.6 L4.4 8.4 L9.8 2.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );

  let tooltip = "";
  let node: ReactElement;

  if (!hasMsgId && !status) {
    // Send never left our end — Interakt returned an error at request time
    // so there is no message id to track and no webhook will ever arrive.
    tooltip = "Send failed — Interakt did not accept the message";
    node = (
      <span className="inline-flex items-center gap-1 text-[#A61414]">
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
          <path
            d="M3 3 L9 9 M9 3 L3 9"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
        <span className="text-[10px] font-gilroy-medium">Not sent</span>
      </span>
    );
  } else if (status === "FAILED") {
    tooltip = "Delivery failed";
    node = (
      <span className="inline-flex items-center gap-1 text-[#A61414]">
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
          <path
            d="M3 3 L9 9 M9 3 L3 9"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
        <span className="text-[10px] font-gilroy-medium">Failed</span>
      </span>
    );
  } else if (status === "SENT") {
    tooltip = "Sent";
    node = (
      <span className="inline-flex text-[#7B7B7B]" aria-label="Sent">
        <svg width="14" height="11" viewBox="0 0 11 11" aria-hidden>
          {tickPath}
        </svg>
      </span>
    );
  } else if (status === "DELIVERED") {
    tooltip = "Delivered";
    node = (
      <span className="inline-flex text-[#7B7B7B]" aria-label="Delivered">
        <svg width="18" height="11" viewBox="0 0 15 11" aria-hidden>
          {tickPath}
          <g transform="translate(4,0)">{tickPath}</g>
        </svg>
      </span>
    );
  } else if (status === "READ") {
    tooltip = "Read";
    node = (
      <span className="inline-flex text-[#34B7F1]" aria-label="Read">
        <svg width="18" height="11" viewBox="0 0 15 11" aria-hidden>
          {tickPath}
          <g transform="translate(4,0)">{tickPath}</g>
        </svg>
      </span>
    );
  } else {
    // Row predates the delivery_status column. Nothing reliable to show.
    tooltip = "Delivery status not tracked";
    node = <span className="text-[#C4C4C4] text-[12px]">—</span>;
  }

  // Append the timestamp to the tooltip when we have one, so hovering tells
  // the admin not just "Read" but "Read at 6:42 PM, 8th June".
  const fullTip = at ? `${tooltip} · ${formatBriefDateTime(at)}` : tooltip;
  return <Tooltip title={fullTip}>{node}</Tooltip>;
};

type BriefDetail = {
  id: string;
  title: string;
  description?: string | null;
  category_groups: string[];
  city: string;
  state: string;
  project_type?: string;
  project_area?: string | null;
  current_status?: string | null;
  urgency?: string | null;
  contact_name: string;
  contact_phone: string;
  contact_email?: string | null;
  poster_user_id?: string;
  poster_type?: string;
  status: keyof typeof STATUS_DISPLAY | string;
  interested_count: number;
  admin_comment?: string | null;
  created_at: string;
  approved_at?: string | null;
  expires_at?: string | null;
  extension_count: number;
  extension_prompted?: boolean;
  media_files: BriefMedia[];
  interests: BriefInterest[];
};

function BriefDetails() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const [pageLoading, setPageLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [briefData, setBriefData] = useState<BriefDetail | null>(null);

  // Review flow modals
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchBrief = (briefId: string) => {
    setPageLoading(true);
    return getBriefDetails(briefId)
      .then((res: any) => {
        const detail = res?.data as BriefDetail;
        if (!detail || !detail.id) {
          setNotFound(true);
          setBriefData(null);
        } else {
          setBriefData(detail);
          setNotFound(false);
        }
      })
      .catch((err: any) => {
        if (err?.status === 404) {
          setNotFound(true);
        } else {
          toast.error("Could not load brief details.");
        }
        setBriefData(null);
      })
      .finally(() => setPageLoading(false));
  };

  useEffect(() => {
    if (!id) {
      setPageLoading(false);
      setNotFound(true);
      return;
    }
    void fetchBrief(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Split media list once: backend sends a single array tagged with file_type.
  const { imageFiles, documentFiles } = useMemo(() => {
    const all = briefData?.media_files || [];
    return {
      imageFiles: all.filter((f) => f.file_type === "image"),
      documentFiles: all.filter((f) => f.file_type === "document"),
    };
  }, [briefData]);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const res: any = await reviewBrief(id, { status: "APPROVED" });
      const message =
        res?.data?.message || "Brief approved and broadcast sent.";
      toast.success(message);
    } catch (err: any) {
      const errMsg =
        err?.data?.errors?.general?.[0] ||
        err?.data?.errors?.status?.[0] ||
        "Approval is taking longer than expected — syncing the latest status. If it didn't go through, please retry.";
      toast.error(errMsg);
    } finally {
      // Always close the modal and re-sync to the server's true state. Approve
      // runs the WhatsApp broadcast synchronously, so a slow or timed-out
      // request can leave the brief APPROVED server-side even when the call
      // appears to fail. Refetching unconditionally prevents the stuck-modal /
      // stale-page symptom (where only a manual refresh showed the update).
      setSubmitting(false);
      setShowApproveConfirm(false);
      void fetchBrief(id);
    }
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      setRejectError("Please add a reason for rejecting this brief.");
      return;
    }
    setSubmitting(true);
    try {
      const res: any = await reviewBrief(id, {
        status: "REJECTED",
        admin_comment: rejectComment.trim(),
      });
      const message = res?.data?.message || "Brief rejected.";
      toast.success(message);
      setShowRejectModal(false);
      setRejectComment("");
      setRejectError("");
      await fetchBrief(id);
    } catch (err: any) {
      const errMsg =
        err?.data?.errors?.admin_comment?.[0] ||
        err?.data?.errors?.general?.[0] ||
        err?.data?.errors?.status?.[0] ||
        "Could not reject brief. Please try again.";
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Download a file under its DISPLAY name rather than the storage object's
  // UUID path. The <a download> attribute is ignored for cross-origin URLs, so
  // fetch the bytes into a blob first and download that. If the fetch is
  // blocked (e.g. the bucket has no CORS rule for this origin), fall back to
  // opening the URL so the download still works (with the object name).
  const handleDownload = async (url?: string | null, filename?: string | null) => {
    if (!url) return;
    const name = filename || "download";
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  if (notFound) {
    return <Notfound redirectURL="/briefs" redirectLabel="Go back to Briefs" />;
  }

  if (pageLoading || !briefData) {
    return (
      <div className="h-[70vh] flex justify-center items-center">
        <div className="text-md font-medium text-gray-900 bg-white rounded-lg inline-flex items-center gap-3">
          <img src={loadingSpinner} alt="spinner" className="h-auto w-[10vh]" />
        </div>
      </div>
    );
  }

  const display =
    STATUS_DISPLAY[briefData.status as string] || STATUS_DISPLAY.PENDING;
  const isPending = briefData.status === "PENDING";
  const location = [briefData.city, briefData.state]
    .filter(Boolean)
    .join(", ");
  const area = formatArea(briefData.project_area);
  const postedAt = formatDateLong(briefData.created_at);
  const approvedAt = formatDateLong(briefData.approved_at);
  const expiresAt = formatDateLong(briefData.expires_at);

  return (
    <div className="w-full flex flex-col h-auto">
      {/* Top breadcrumb / back row — slim, matches admin top-strip pattern. */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-c-white-3">
        <button
          type="button"
          onClick={() => navigate("/briefs")}
          className="flex items-center gap-1 font-gilroy-medium text-[13px] text-pot-grey5 hover:opacity-70"
        >
          <LeftArrowIcon />
          <span>Back to Briefs</span>
        </button>
        <div className="flex items-center gap-2 font-gilroy-medium text-[12px]">
          <span className="text-[#7B7B7B]">Brief ID:</span>
          <span className="text-pot-grey5">{briefData.id.slice(0, 8)}</span>
        </div>
      </div>

      {/* PENDING banner — drives attention to the action needed. */}
      {isPending && (
        <div className="px-4 py-3 bg-[#FFF7E5] border-b border-[#FFE2A6] flex items-center justify-between gap-4">
          <div>
            <p className="font-gilroy-semi-bold text-[13px] text-[#7A5800]">
              Awaiting your review
            </p>
            <p className="font-gilroy-medium text-[12px] text-[#8B6A00] mt-0.5">
              Approve to broadcast this brief to matching professionals, or
              reject with a reason for the customer.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              label="Reject"
              variant="secondary-outline"
              onClick={() => {
                setRejectComment("");
                setRejectError("");
                setShowRejectModal(true);
              }}
            />
            <Button
              label="Approve"
              variant="primary"
              onClick={() => setShowApproveConfirm(true)}
            />
          </div>
        </div>
      )}

      <div className="flex flex-row">
        {/* Left column — primary content */}
        <div className="w-[78%] border-r border-c-white-3 py-3 px-4 flex flex-col gap-4">
          {/* Hero card — Project Details */}
          <div className="border border-c-white-3">
            <div className="bg-[#FBFBFB] h-[48px] px-5 py-2 border-b border-c-white-3 flex items-center justify-between">
              <p className="font-gilroy-semi-bold text-[14px] leading-5">
                Project Details
              </p>
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-md text-[12px] font-gilroy-medium px-3 py-0.5 ${display.pillClass}`}
                >
                  {display.label}
                </span>
                {briefData.extension_count > 0 && (
                  <span className="rounded-md text-[12px] font-gilroy-medium px-3 py-0.5 bg-[#E8F1FF] text-[#1759AC]">
                    Extended × {briefData.extension_count}
                  </span>
                )}
              </div>
            </div>
            <div className="p-5">
              <h2 className="font-gilroy-semi-bold text-[18px] leading-[1.3] tracking-[-0.2px] text-pot-grey5">
                {briefData.title || "Untitled brief"}
              </h2>

              {/* Meta row — same admin Label: value pattern, just compact */}
              <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4 text-[13px] items-center">
                {briefData.project_type && (
                  <div className="flex items-center gap-2">
                    <span className="text-[#7B7B7B] font-gilroy-medium">
                      Project Type:
                    </span>
                    <span className="font-gilroy-semi-bold text-pot-grey5">
                      {briefData.project_type}
                    </span>
                  </div>
                )}
                {location && (
                  <div className="flex items-center gap-2">
                    <span className="text-[#7B7B7B] font-gilroy-medium">
                      Location:
                    </span>
                    <span className="font-gilroy-semi-bold text-pot-grey5">
                      {location}
                    </span>
                  </div>
                )}
                {area && (
                  <div className="flex items-center gap-2">
                    <span className="text-[#7B7B7B] font-gilroy-medium">
                      Area:
                    </span>
                    <span className="font-gilroy-semi-bold text-pot-grey5">
                      {area}
                    </span>
                  </div>
                )}
                {briefData.urgency && (
                  <div className="flex items-center gap-2">
                    <span className="text-[#7B7B7B] font-gilroy-medium">
                      Urgency:
                    </span>
                    <span className="font-gilroy-semi-bold text-pot-grey5 px-3 py-0.5 bg-[#EBEBEB] rounded-md">
                      {briefData.urgency}
                    </span>
                  </div>
                )}
              </div>

              {/* Description */}
              {briefData.description && (
                <div className="mt-5 pt-5 border-t border-c-white-3">
                  <p className="text-[12px] text-[#7B7B7B] font-gilroy-semi-bold mb-2">
                    Description
                  </p>
                  <p className="text-[14px] text-pot-grey5 font-gilroy-regular leading-[1.7] max-w-[90%]">
                    {briefData.description}
                  </p>
                </div>
              )}

              {/* Categories — square admin chips */}
              <div className="mt-5 pt-5 border-t border-c-white-3">
                <p className="text-[12px] text-[#7B7B7B] font-gilroy-semi-bold mb-2.5">
                  Professional Categories
                </p>
                {(briefData.category_groups || []).length === 0 ? (
                  <p className="text-[13px] text-[#9A9A9A] font-gilroy-medium">
                    None specified
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {(briefData.category_groups || []).map((category, index) => (
                      <span
                        key={`${category}-${index}`}
                        className="px-3 py-1 rounded-none text-[12px] border border-[#DEDEDE] bg-[#FCFCFC] text-[#1C1C1C]"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Project stage (current_status) */}
              {briefData.current_status && (
                <div className="mt-5 pt-5 border-t border-c-white-3 flex items-center gap-3">
                  <span className="text-[12px] text-[#7B7B7B] font-gilroy-semi-bold">
                    Project Stage:
                  </span>
                  <span className="font-gilroy-semi-bold text-pot-grey5 px-3 py-0.5 border border-c-white-3 rounded-md text-[12px]">
                    {briefData.current_status}
                  </span>
                </div>
              )}

              {/* Lifecycle dates strip */}
              <div className="mt-5 pt-5 border-t border-c-white-3 flex flex-wrap gap-x-10 gap-y-2 text-[12px]">
                {postedAt && (
                  <div>
                    <p className="text-[#7B7B7B] font-gilroy-medium">Posted</p>
                    <p className="text-pot-grey5 font-gilroy-semi-bold mt-0.5">
                      {postedAt}
                    </p>
                  </div>
                )}
                {approvedAt && (
                  <div>
                    <p className="text-[#7B7B7B] font-gilroy-medium">Approved</p>
                    <p className="text-pot-grey5 font-gilroy-semi-bold mt-0.5">
                      {approvedAt}
                    </p>
                  </div>
                )}
                {expiresAt && (
                  <div>
                    <p className="text-[#7B7B7B] font-gilroy-medium">Expires</p>
                    <p className="text-pot-grey5 font-gilroy-semi-bold mt-0.5">
                      {expiresAt}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Rejection reason callout (REJECTED only) */}
          {briefData.status === "REJECTED" && briefData.admin_comment && (
            <div className="border border-[#C02222] bg-[#FFF1F1] p-4">
              <p className="font-gilroy-semi-bold text-[13px] text-[#C02222]">
                Reason for rejection
              </p>
              <p className="mt-1 font-gilroy-regular text-[13px] leading-[150%] text-pot-grey5">
                {briefData.admin_comment}
              </p>
            </div>
          )}

          {/* Files card */}
          {(imageFiles.length > 0 || documentFiles.length > 0) && (
            <div className="border border-c-white-3">
              <div className="bg-[#FBFBFB] h-[48px] px-5 py-2 border-b border-c-white-3 flex items-center justify-between">
                <p className="font-gilroy-semi-bold text-[14px] leading-5">
                  Project Files
                </p>
                <span className="text-[12px] text-[#7B7B7B] font-gilroy-medium">
                  {imageFiles.length} image{imageFiles.length === 1 ? "" : "s"}
                  {" · "}
                  {documentFiles.length} document
                  {documentFiles.length === 1 ? "" : "s"}
                </span>
              </div>

              {imageFiles.length > 0 && (
                <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {imageFiles.map((file, index) => (
                    <button
                      type="button"
                      key={file?.id || index}
                      onClick={() =>
                        handleDownload(file?.download_url, file?.filename)
                      }
                      className="block text-left w-full border border-c-white-3 hover:border-pot-yellow transition-colors cursor-pointer"
                      aria-label={`Download ${file?.filename || "image"}`}
                    >
                      <div className="relative w-full pb-[72%] bg-[#F5F5F5]">
                        {file?.download_url ? (
                          <img
                            src={file.download_url}
                            alt={file?.filename || "brief-media"}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-[#A1A1A1] text-[11px]">
                            No Preview
                          </div>
                        )}
                      </div>
                      <div className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <FileIcon color="#242424" />
                          <p className="text-[11px] font-gilroy-semi-bold truncate">
                            {file?.filename || "Unknown_file"}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {documentFiles.length > 0 && (
                <div
                  className={`p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ${
                    imageFiles.length > 0 ? "border-t border-c-white-3" : ""
                  }`}
                >
                  {documentFiles.map((file, index) => (
                    <div
                      key={file?.id || index}
                      className="flex items-center gap-2 border border-c-white-3 px-3 py-2 hover:border-pot-yellow transition-colors"
                    >
                      <PDFFileIcon width="20" />
                      <div className="min-w-0 flex-1">
                        <p
                          className="text-[12px] font-gilroy-semi-bold truncate"
                          title={file?.filename}
                        >
                          {file?.filename || "Unknown_file"}
                        </p>
                        <p className="text-[10px] text-[#7C7C7C] font-gilroy-medium mt-0.5">
                          {fileExtension(file?.file || "") || "DOC"}
                        </p>
                      </div>
                      <Tooltip title="Download">
                        <button
                          type="button"
                          onClick={() =>
                            handleDownload(file?.download_url, file?.filename)
                          }
                          className="shrink-0"
                          aria-label={`Download ${file?.filename || "file"}`}
                        >
                          <DownloadIcon color="black" size="12" />
                        </button>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Interested professionals — only meaningful once approved */}
          {briefData.status !== "PENDING" &&
            briefData.status !== "REJECTED" && (
              <div className="border border-c-white-3">
                <div className="bg-[#FBFBFB] h-[48px] px-5 py-2 border-b border-c-white-3 flex items-center justify-between">
                  <p className="font-gilroy-semi-bold text-[14px] leading-5">
                    Interested Professionals ({briefData.interested_count ?? 0})
                  </p>
                  <span className="text-[12px] text-[#7B7B7B] font-gilroy-medium">
                    {(briefData.interests || []).length} broadcast
                  </span>
                </div>
                {(briefData.interests || []).length === 0 ? (
                  <div className="p-6 text-center text-[13px] text-[#7B7B7B] font-gilroy-medium">
                    No professionals have responded yet.
                  </div>
                ) : (
                  <div className="divide-y divide-c-white-3">
                    {(briefData.interests || []).map((interest) => {
                      const intDisp =
                        INTEREST_DISPLAY[interest.response] ||
                        INTEREST_DISPLAY.PENDING;
                      return (
                        <div
                          key={interest.id}
                          className="px-5 py-3 flex items-center justify-between gap-4"
                        >
                          <div className="min-w-0">
                            <p className="text-[13px] font-gilroy-semi-bold text-pot-grey5 truncate">
                              {interest.professional_name ||
                                "Unnamed professional"}
                            </p>
                            {interest.responded_at && (
                              <p className="text-[11px] text-[#7B7B7B] font-gilroy-medium mt-0.5">
                                Responded{" "}
                                {formatBriefDateTime(interest.responded_at)}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-3 whitespace-nowrap">
                            <DeliveryTicks
                              status={interest.delivery_status}
                              hasMsgId={!!interest.wa_msg_id}
                              at={interest.delivery_status_at}
                            />
                            <span
                              className={`rounded-md text-[11px] font-gilroy-medium px-3 py-0.5 ${intDisp.className}`}
                            >
                              {intDisp.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Right column — Customer Details */}
        <div className="w-[22%] py-3 px-4 flex flex-col gap-4">
          <div className="border border-c-white-3">
            <div className="bg-[#FBFBFB] h-[48px] px-5 py-2 border-b border-c-white-3 flex items-center justify-between">
              <p className="font-gilroy-semi-bold text-[14px] leading-5 text-pot-yellow">
                Customer Details
              </p>
              {briefData.poster_type && (
                <span className="text-[10px] text-[#7B7B7B] font-gilroy-semi-bold tracking-[0.5px]">
                  {briefData.poster_type}
                </span>
              )}
            </div>
            <div className="p-4 space-y-3">
              <div>
                <p className="text-[11px] text-[#7C7C7C] font-gilroy-medium">
                  Name
                </p>
                <p className="text-[13px] font-gilroy-semi-bold text-pot-grey5 mt-0.5 break-words">
                  {briefData.contact_name || "-"}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-[#7C7C7C] font-gilroy-medium">
                  WhatsApp / Phone
                </p>
                <p className="text-[13px] font-gilroy-semi-bold text-pot-grey5 mt-0.5 break-all">
                  {briefData.contact_phone || "-"}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-[#7C7C7C] font-gilroy-medium">
                  Email Address
                </p>
                <p className="text-[13px] font-gilroy-semi-bold text-pot-grey5 mt-0.5 break-all">
                  {briefData.contact_email || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Extension prompt — only when relevant */}
          {briefData.extension_prompted && (
            <div className="border border-[#FFE2A6] bg-[#FFF7E5] p-3">
              <p className="font-gilroy-semi-bold text-[12px] text-[#7A5800]">
                Extension prompted
              </p>
              <p className="mt-1 text-[12px] text-[#8B6A00] font-gilroy-medium leading-[1.5]">
                Customer was asked whether they want to extend this brief.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Approve confirmation */}
      {showApproveConfirm && (
        <ConfirmationModal
          header="Approve this brief?"
          description="Approving will publish this brief and broadcast it to matching professionals via WhatsApp. This action cannot be undone."
          confirmButtonLabel={submitting ? "Approving..." : "Yes, Approve"}
          cancelButtonLabel="Cancel"
          onConfirm={() => {
            if (!submitting) void handleApprove();
          }}
          onCancel={() => {
            if (!submitting) setShowApproveConfirm(false);
          }}
        />
      )}

      {/* Reject modal — comment required */}
      {showRejectModal && (
        <Modal
          top
          header="Reject this brief"
          handleCancel={() => {
            if (!submitting) {
              setShowRejectModal(false);
              setRejectComment("");
              setRejectError("");
            }
          }}
          clickOutsideToClose
          footer={
            <div className="flex gap-x-3">
              <Button
                variant="primary"
                label={submitting ? "Rejecting..." : "Reject Brief"}
                disabled={submitting}
                onClick={() => {
                  if (!submitting) void handleReject();
                }}
              />
              <Button
                variant="secondary"
                label="Cancel"
                disabled={submitting}
                onClick={() => {
                  if (!submitting) {
                    setShowRejectModal(false);
                    setRejectComment("");
                    setRejectError("");
                  }
                }}
              />
            </div>
          }
        >
          <div className="w-full">
            <p className="text-[13px] text-[#7B7B7B] mb-3 font-gilroy-medium leading-[1.5]">
              Add a reason for rejection. The customer will see this message.
            </p>
            <TextArea
              label="Reason"
              required
              rows={4}
              placeholder="e.g. Insufficient project details. Please add description and area."
              value={rejectComment}
              onChange={(val: string) => {
                setRejectComment(val);
                if (rejectError) setRejectError("");
              }}
              // The shared TextArea renders `hint` for inline messages (the
              // `error` prop exists in the type but isn't displayed).
              hint={rejectError}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}

export default BriefDetails;
